import { useState, type ReactNode } from 'react';
import type { PeerAssessableAttribute, PeerAssessmentColleague, PeerAssessmentsResponse } from '../api/types';
import { describeError } from '../api/client';
import { useApiResource } from '../hooks/useApiResource';
import { EmptyState, ErrorState, LoadingState } from '../components/StateMessage';
import { PeerAssessmentForm } from '../components/peer/PeerAssessmentForm';
import { PeerWithdrawalForm } from '../components/peer/PeerWithdrawalForm';
import styles from './PeerAssessmentsPage.module.css';

/**
 * Peer Assessments — the read and write surface for `GET /peer-assessments`
 * and the existing assessment write path
 * (`docs/DOMAIN_DECISIONS.md` "Peer Visibility Scope", PV-1 through PV-6).
 * A dedicated nav destination, not a profile section, for the same reason
 * the Assessments Queue is one: the data is caller-scoped rather than
 * attached to any one employee's profile, so it has nowhere to nest inside
 * `EmployeeProfilePage` (§5i's distinction).
 *
 * ---------------------------------------------------------------------
 * WHAT THIS PAGE IS ALLOWED TO SAY
 *
 * Exactly PV-2, and nothing PV-3 names as hidden. For each eligible
 * colleague: their name and id, each attribute the caller is permitted to
 * peer-assess (already filtered server-side to standard sensitivity and
 * peer-permitted), that attribute's scale wording, and the caller's own
 * prior rating of it for that colleague, if any.
 *
 * There is no composite/overall rating, no confidence or variance, no
 * history or trajectory, no Role Fit, no other assessor's identity or
 * rating, and no Elevated attribute — none of that exists anywhere
 * upstream of the endpoint for this page to render, and the page must not
 * invent a substitute for any of it.
 *
 * The colleague's name is deliberately plain text, never a link to their
 * profile: general profile and directory visibility is not granted by peer
 * eligibility (PV-4), and `/people/:employeeId` still enforces the
 * unmodified `authorizeEmployeeAccess` — linking there would send an
 * eligible peer to a 404 for someone this very page can name.
 *
 * ---------------------------------------------------------------------
 * WRITING: RECORD, CORRECT, WITHDRAW — YOUR OWN, ONLY
 *
 * Every attribute this page lists is one `GET /peer-assessments` has
 * already established the caller may assess (PV-2), so there is no
 * `canAssess` gate here the way the profile's attribute rows have one —
 * offering the affordance is never dishonest. "Rate" (no `ownAssessmentId`
 * yet) becomes "Correct" once one exists; "Withdraw" appears alongside it.
 * Both reuse the existing write endpoints unchanged
 * (`POST .../assessments`, `POST .../assessments/:id/withdrawal`) via
 * `PeerAssessmentForm`/`PeerWithdrawalForm` — no new backend behaviour, and
 * the server re-checks authorship on every one of these calls regardless
 * of what this page offers.
 *
 * Only one action is open at a time, tracked by (colleague, attribute) —
 * the same "one half-finished lifecycle action at a time" rule the
 * profile's `AssessmentRecords` already applies. On success the page
 * re-reads `/peer-assessments` rather than patching a rating or an id into
 * local state, matching this repository's "re-read, never adjust"
 * discipline for every other write path.
 * ---------------------------------------------------------------------
 */
export function PeerAssessmentsPage() {
  const state = useApiResource<PeerAssessmentsResponse>('/peer-assessments');
  const [acting, setActing] = useState<{
    colleagueId: string;
    attributeId: string;
    mode: 'assess' | 'withdraw';
  } | null>(null);

  if (state.status === 'loading') return <LoadingState label="Loading your peer assessments…" />;

  if (state.status === 'failed') {
    return <ErrorState title="Peer assessments could not be loaded" detail={describeError(state.error)} />;
  }

  const { colleagues } = state.data;

  function finish() {
    setActing(null);
    state.reload();
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.heading}>Peer Assessments</h1>
      </header>

      <p className={styles.note}>
        Colleagues you currently share an active team with, and the attributes you are permitted to assess for them.
        Where you have already rated an attribute for someone, your own rating is shown alongside it, exactly as you
        last recorded it, with the option to correct or withdraw it.
      </p>

      {colleagues.length === 0 ? (
        <EmptyState
          title="No peers to assess"
          detail="You are not currently eligible to peer-assess anyone. Eligibility follows active team membership, and changes automatically as team assignments change."
        />
      ) : (
        <ul className={styles.list} aria-label="Colleagues you can peer-assess">
          {colleagues.map((colleague) => (
            <ColleagueCard key={colleague.id} colleague={colleague} acting={acting} setActing={setActing} onDone={finish} />
          ))}
        </ul>
      )}
    </div>
  );
}

type ActingState = { colleagueId: string; attributeId: string; mode: 'assess' | 'withdraw' } | null;

function ColleagueCard({
  colleague,
  acting,
  setActing,
  onDone,
}: {
  colleague: PeerAssessmentColleague;
  acting: ActingState;
  setActing: (next: ActingState) => void;
  onDone: () => void;
}) {
  // Not-yet-rated first — that is the actionable state this page exists to
  // let someone act on — then already-rated attributes, where the caller's
  // own work is already done and correcting or withdrawing it is secondary.
  // Neither group is reordered internally; only the two groups are split.
  const unrated = colleague.attributes.filter((a) => a.ownAssessmentId === null);
  const rated = colleague.attributes.filter((a) => a.ownAssessmentId !== null);

  function renderItem(attribute: PeerAssessableAttribute) {
    return (
      <AttributeItem
        key={attribute.id}
        colleagueId={colleague.id}
        colleagueName={colleague.fullName}
        attribute={attribute}
        acting={acting && acting.colleagueId === colleague.id && acting.attributeId === attribute.id ? acting.mode : null}
        onAct={(mode) => setActing({ colleagueId: colleague.id, attributeId: attribute.id, mode })}
        onCancel={() => setActing(null)}
        onDone={onDone}
      />
    );
  }

  return (
    <li className={styles.group}>
      <span className={styles.person}>{colleague.fullName}</span>
      {colleague.attributes.length === 0 ? (
        <p className={styles.noAttributes}>No attributes are currently open for you to assess for this colleague.</p>
      ) : (
        <>
          {unrated.length > 0 ? (
            <CollapsibleAttributeList attributes={unrated} renderItem={renderItem} />
          ) : null}
          {rated.length > 0 ? (
            <>
              {unrated.length > 0 ? (
                <p className={styles.ratedLabel}>Already rated ({rated.length})</p>
              ) : null}
              <CollapsibleAttributeList attributes={rated} renderItem={renderItem} />
            </>
          ) : null}
        </>
      )}
    </li>
  );
}

/** Rows shown before "Show N more" (Visual QA Round 2) — the same length
 * limit the Assessments Queue applies, for the same reason: nothing here is
 * ranked or filtered by value, only the count shown by default is capped,
 * and every attribute stays one click away. */
const DEFAULT_VISIBLE = 6;

function CollapsibleAttributeList({
  attributes,
  renderItem,
}: {
  attributes: PeerAssessableAttribute[];
  renderItem: (attribute: PeerAssessableAttribute) => ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? attributes : attributes.slice(0, DEFAULT_VISIBLE);
  const hiddenCount = attributes.length - visible.length;

  return (
    <>
      <ul className={styles.items}>{visible.map(renderItem)}</ul>
      {attributes.length > DEFAULT_VISIBLE ? (
        <button
          type="button"
          className={styles.moreToggle}
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? 'Show fewer' : `Show ${hiddenCount} more`}
        </button>
      ) : null}
    </>
  );
}

function AttributeItem({
  colleagueId,
  colleagueName,
  attribute,
  acting,
  onAct,
  onCancel,
  onDone,
}: {
  colleagueId: string;
  colleagueName: string;
  attribute: PeerAssessableAttribute;
  acting: 'assess' | 'withdraw' | null;
  onAct: (mode: 'assess' | 'withdraw') => void;
  onCancel: () => void;
  onDone: () => void;
}) {
  return (
    <li className={styles.item}>
      <div className={styles.attributeHead}>
        <span className={styles.attribute}>{attribute.name}</span>
        <span className={styles.rating}>
          {attribute.ownRating === null ? (
            <span className={styles.notRated}>You haven&rsquo;t rated this yet</span>
          ) : (
            `Your rating: ${attribute.ownRating}`
          )}
        </span>
      </div>
      <p className={styles.scaleHint}>
        <span>1 — {attribute.lowEndMeaning}</span>
        <span>20 — {attribute.highEndMeaning}</span>
      </p>

      {acting === null ? (
        <span className={styles.actions}>
          <button type="button" className={styles.action} onClick={() => onAct('assess')}>
            {attribute.ownAssessmentId === null ? 'Rate' : 'Correct'}
          </button>
          {attribute.ownAssessmentId !== null ? (
            <button type="button" className={styles.action} onClick={() => onAct('withdraw')}>
              Withdraw
            </button>
          ) : null}
        </span>
      ) : null}

      {acting === 'assess' ? (
        <PeerAssessmentForm
          employeeId={colleagueId}
          colleagueName={colleagueName}
          attribute={attribute}
          onRecorded={onDone}
          onClose={onCancel}
        />
      ) : null}

      {acting === 'withdraw' && attribute.ownAssessmentId !== null && attribute.ownRating !== null ? (
        <PeerWithdrawalForm
          employeeId={colleagueId}
          attributeId={attribute.id}
          attributeName={attribute.name}
          assessmentId={attribute.ownAssessmentId}
          currentRating={attribute.ownRating}
          onWithdrawn={onDone}
          onClose={onCancel}
        />
      ) : null}
    </li>
  );
}
